import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import { toast } from "sonner";
import type { BacklogItem } from "@/types/backlog";

interface DuplicateInput {
  source: BacklogItem;
  targetSubsheetIds?: string[] | null;
  insertPosition?: number | null;
  emitToast?: boolean;
}

interface DuplicateResult {
  newItemId: string;
  newTicketNumber: number;
  linkCreated: boolean;
}

export const useDuplicateBacklogItem = () => {
  const queryClient = useQueryClient();

  return useMutation<DuplicateResult, Error, DuplicateInput>({
    mutationFn: async ({ source, targetSubsheetIds, insertPosition = null, emitToast = true }) => {
      const subsheetIds = targetSubsheetIds ?? source.subsheet_ids;

      const { data: newItem, error: insertError } = await supabase.rpc("insert_backlog_item", {
        _task: source.task,
        _description: source.description || null,
        _assigned_to_id: source.assigned_to_id && source.assigned_to_id.length > 0 ? source.assigned_to_id : null,
        _status_id: source.status_id || null,
        _priority_id: source.priority_id || null,
        _attachments: [],
        _insert_position: insertPosition,
        _team_ids: source.team_ids ?? [],
        _assigned_team_id: source.assigned_team_id ?? null,
        _subsheet_ids: subsheetIds && subsheetIds.length > 0 ? subsheetIds : null,
        _ticket_type_id: source.ticket_type_id || null,
        // The copy keeps the source's pipeline, as it keeps its status (#9304);
        // a source with none stays with none rather than taking a default.
        _pipeline_id: source.pipeline_id ?? null,
        _no_pipeline: !source.pipeline_id,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase generated types omit the extended RPC overload params
      } as any);

      if (insertError) throw insertError;
      if (!newItem) throw new Error("No data returned from insert");

      // The RPC returns the inserted row.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const createdItem = newItem as any;
      const newItemId: string = createdItem.id;
      const newTicketNumber: number = createdItem.ticket_number ?? 0;

      let linkCreated = false;
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { error: linkError } = await supabase.from("backlog_item_links").insert({
          source_item_id: source.id,
          target_item_id: newItemId,
          link_type: "related_to",
          created_by_id: user.id,
        });
        if (linkError) {
          console.error("Failed to create related_to link for duplicate:", linkError);
          if (emitToast) {
            toast.warning("Item duplicated, but link to source failed");
          }
        } else {
          linkCreated = true;
        }
      }

      queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item-links", source.id] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item-links", newItemId] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item-link-counts"] });

      if (emitToast && linkCreated) {
        toast.success("Item duplicated");
      }

      return { newItemId, newTicketNumber, linkCreated };
    },
    onError: (error) => {
      console.error("Failed to duplicate item:", error);
      toast.error(`Failed to duplicate: ${error.message}`);
    },
  });
};
